import { ReactNode, useRef, useState, type CSSProperties, type Dispatch, type MouseEvent, type SetStateAction } from "react";
import { Button, Dropdown, Menu, Checkbox } from "antd";
import clsx from "clsx";
import { CloseIcon, PlusIconBlack } from "@/shared/assets/svg/icons";
import "./input-select.scss";

export type IStatus = {
  status: string;
  icon?: ReactNode;
  id: string;
  count?: number;
};

interface IInputSelect {
  title: string;
  onClick?: () => void;
  style?: CSSProperties;
  statuses?: IStatus[];

  selectedStatus?: IStatus | null;
  setSelectedStatus?: (status: IStatus | null) => void;

  isMultiple?: boolean;
  selectedStatuses?: string[];
  setSelectedStatuses?: Dispatch<SetStateAction<string[]>>;

  theme?: "default" | "transparent";

  showAllOption?: boolean;
  allOptionLabel?: string;
  renderMenuItem?: (args: { status: IStatus; checked: boolean }) => ReactNode;
  renderSelectedItem?: (args: { status: IStatus; onRemove: () => void }) => ReactNode;
}

export const InputSelect = ({
  title,
  onClick,
  style,
  statuses = [],
  selectedStatus,
  setSelectedStatus,
  isMultiple = false,
  selectedStatuses = [],
  setSelectedStatuses,
  theme = "default",
  showAllOption = false,
  allOptionLabel = "Все",
  renderMenuItem,
  renderSelectedItem,
}: IInputSelect) => {
  const [isOpen, setIsOpen] = useState(false);
  const ignoreNextCloseRef = useRef(false);

  const handleSelectStatus = (status: IStatus) => {
    if (setSelectedStatus) {
      setSelectedStatus(status);
    }
    setIsOpen(false);
  };

  const handleRemoveStatus = (e?: MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (setSelectedStatus) {
      setSelectedStatus(null);
    }
  };

  const toggleMultipleStatus = (statusId: string) => {
    if (!setSelectedStatuses) return;
    const updated = selectedStatuses.includes(statusId)
      ? selectedStatuses.filter((id) => id !== statusId)
      : [...selectedStatuses, statusId];
    setSelectedStatuses(updated);
  };

  const isAllChecked = isMultiple && statuses.length > 0 && selectedStatuses.length === statuses.length;
  const isAllIndeterminate =
    isMultiple && selectedStatuses.length > 0 && selectedStatuses.length < statuses.length;

  const menu = (
    <Menu className="input-select-menu">
      {isMultiple && showAllOption && (
        <Menu.Item
          key="__all__"
          className="input-select-menu__item"
          onClick={(info) => {
            ignoreNextCloseRef.current = true;
            info?.domEvent?.preventDefault?.();
            info?.domEvent?.stopPropagation?.();

            if (!setSelectedStatuses) return;

            if (isAllChecked) {
              setSelectedStatuses([]);
            } else {
              setSelectedStatuses(statuses.map((s) => s.id));
            }
            setIsOpen(true);
          }}
        >
          <div className="input-select-menu__row">
            <Checkbox
              checked={isAllChecked}
              indeterminate={isAllIndeterminate}
              onClick={(e) => {
                ignoreNextCloseRef.current = true;
                e.stopPropagation();
                e.preventDefault();
                if (!setSelectedStatuses) return;
                if (isAllChecked) {
                  setSelectedStatuses([]);
                } else {
                  setSelectedStatuses(statuses.map((s) => s.id));
                }
                setIsOpen(true);
              }}
            />
            <span className="input-select-menu__label">{allOptionLabel}</span>
          </div>
        </Menu.Item>
      )}
      {statuses.map((status) => {
        const isChecked = isMultiple
          ? selectedStatuses.includes(status.id)
          : selectedStatus?.id === status.id;

        return (
          <Menu.Item
            key={status.id}
            className="input-select-menu__item"
            onClick={(info) => {
              if (isMultiple) {
                ignoreNextCloseRef.current = true;
                info?.domEvent?.preventDefault?.();
                info?.domEvent?.stopPropagation?.();
                toggleMultipleStatus(status.id);
                setIsOpen(true);
              } else {
                handleSelectStatus(status);
              }
            }}
          >
            <div className="input-select-menu__row">
              {isMultiple && (
                <Checkbox
                  checked={isChecked}
                  onClick={(e) => {
                    ignoreNextCloseRef.current = true;
                    e.stopPropagation();
                    e.preventDefault();
                    toggleMultipleStatus(status.id);
                    setIsOpen(true);
                  }}
                />
              )}
              {renderMenuItem ? (
                <span className="input-select-menu__custom">
                  {renderMenuItem({ status, checked: isChecked })}
                </span>
              ) : (
                <>
                  <span className="input-select-menu__icon">{status.icon}</span>
                  <span className="input-select-menu__label">{status.status}</span>
                </>
              )}
            </div>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  const hasSelection = isMultiple ? selectedStatuses.length > 0 : !!selectedStatus;
  
  let content;

  if (isMultiple) {
    if (selectedStatuses.length > 0) {
      content = (
        <div className="status-list">
          {statuses
            .filter((s) => selectedStatuses.includes(s.id))
            .map((s) => (
              <div key={s.id}>
                {renderSelectedItem ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    {renderSelectedItem({
                      status: s,
                      onRemove: () =>
                        setSelectedStatuses?.(
                          selectedStatuses.filter((id) => id !== s.id),
                        ),
                    })}
                  </span>
                ) : (
                  <div
                    className={clsx("status-item", theme === "transparent" && "status-item--transparent-selected")}
                  >
                    <div>{s.status}</div>
                    <CloseIcon
                      className="status-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStatuses?.(
                          selectedStatuses.filter((id) => id !== s.id),
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      );
    } else {
      content = <PlusIconBlack className="input-select-icon" />;
    }
  } else {
    content = selectedStatus ? (
      <div className="status-list">
        {renderSelectedItem ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {renderSelectedItem({
              status: selectedStatus,
              onRemove: () => handleRemoveStatus(),
            })}
          </span>
        ) : (
          <div className={clsx("status-item", theme === "transparent" && "status-item--transparent-selected")}>
            <div>{selectedStatus.status}</div>
            <CloseIcon className="status-remove" onClick={handleRemoveStatus} />
          </div>
        )}
      </div>
    ) : (
      <PlusIconBlack className="input-select-icon" />
    );
  }

  return (
    <Dropdown
      overlay={menu}
      trigger={["click"]}
      onVisibleChange={(next) => {
        if (isMultiple && !next && ignoreNextCloseRef.current) {
          ignoreNextCloseRef.current = false;
          return;
        }
        ignoreNextCloseRef.current = false;
        setIsOpen(next);
      }}
      placement="bottomLeft"
      visible={isOpen}
      overlayStyle={{ minWidth: "fit-content" }}
    >
      <Button
        className={clsx("input-select-container", hasSelection && "selected", theme === "transparent" && "input-select-container--transparent", theme === "transparent" && hasSelection && "input-select-container--transparent-selected", theme === "transparent" && !hasSelection && "input-select-container--transparent-empty")}
        onClick={onClick}
        style={style}
      >
        <div className="input-select-title">{title}</div>
        {content}
      </Button>
    </Dropdown>
  );
};

